import { Box, Grid, Tabs, Pagination, Text, Notification } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { MainLayout } from "../../layouts/MainLayout";
import { CourseCard } from "../../entities/components/CourseCard";
import Course1 from "../../shared/images/Course-1.png";
import Course2 from "../../shared/images/Course-2.png";
import Course3 from "../../shared/images/Course-3.png";
import Course4 from "../../shared/images/Course-4.png";
import Course5 from "../../shared/images/Course-5.png";
import Course6 from "../../shared/images/Course-6.png";
import Course7 from "../../shared/images/Course-7.png";
import Course8 from "../../shared/images/Course-8.png";
import Course9 from "../../shared/images/Course-9.png";
import { useTranslation } from "react-i18next";
import useCoursesStore from "../../store/useCoursesStore";
import { GetCoursesParams } from "../../types/api";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import useAuthStore from "../../store/useAuthStore";

import { SearchBar } from "../../features/search-bar/SearchBar";
import { useLocation, useNavigate } from "react-router";
import { TabListWithCounts } from "../../utils/components/CoursesTabList";
import { ReusableOnboardingTour } from "../../utils/components/onboardingTour/OnboardingTourWrapper";
import {
  OnboardingTourFocusRevealProps,
  OnboardingTourStep,
} from "@gfazioli/mantine-onboarding-tour";
import { scrollToTop } from "../../utils/helpers";
import useProfileStore from "../../store/useProfileStore";
import { useOnboardingCoursesSteps } from "../../utils/onboardingTourSteps";
import { TourButton } from "../../utils/components/onboardingTour/TourButton";

const images = [
  Course1,
  Course2,
  Course3,
  Course4,
  Course5,
  Course6,
  Course7,
  Course8,
  Course9,
];

export const Courses = () => {
  const laptop = useMediaQuery("(max-width: 1100px)");
  const mobile = useMediaQuery("(max-width: 820px)");
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { access_token, isRefreshing } = useAuthStore();
  const {
    courses,
    currentCourses,
    currentPage,
    currentTab,
    recommendedCourses,
    learningCourses,
    pageInfo,
    filters,
    fetchCourses,
    fetchRecommendedCourses,
    fetchLearningCourses,
    setCurrentCourses,
    setCurrentPage,
    setCurrentTab,
    fetchFilter,
    addLearningCourses,
    removeLearningCourse,
  } = useCoursesStore();
  const location = useLocation();
  const [credentials, setCredentials] = useState<GetCoursesParams>({});

  const [currentInfoPage, setCurrentInfoPage] = useState(1);

  const { profile, tourButtonShow, fetchProfile, profileLanguage } =
    useProfileStore();
  const [started, setStarted] = useState(false);
  const steps = useOnboardingCoursesSteps();

  const [filteredSteps, setFilteredSteps] = useState<OnboardingTourStep[]>([]);

  const filterExistingSteps = (steps: OnboardingTourStep[]) => {
    return currentCourses.length === 0
      ? steps.filter(
          (step) =>
            step.id !== "courses-card" && step.id !== "courses-pagination"
        )
      : steps;
  };

  useLayoutEffect(() => {
    const stepsToShow = filterExistingSteps(steps);
    setFilteredSteps(stepsToShow);
  }, [currentCourses.length, currentPage, currentTab, profileLanguage]);

  useEffect(() => {
    if (!profile) fetchProfile();
  }, []);

  useEffect(() => {
    if (
      profile?.display_preferences?.hide_tour_tooltip_on_courses !== null &&
      profile?.display_preferences?.hide_tour_tooltip_on_courses !== undefined
    )
      setStarted(!profile?.display_preferences?.hide_tour_tooltip_on_courses);
  }, [profile?.display_preferences?.hide_tour_tooltip_on_courses]);

  const focusRevealProps: OnboardingTourFocusRevealProps = {
    popoverProps: {
      arrowSize: 20,
      position: mobile ? "bottom" : "left",
    },
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get("category");
    const topicFromUrl = params.get("topic");

    const fetchCoursesData = async () => {
      if (categoryFromUrl) {
        setCurrentTab("All Courses");
        setCredentials((prev) => ({
          ...prev,
          category: categoryFromUrl,
          topic: topicFromUrl || "", // передаємо тему, якщо вона є
        }));
        const res = await fetchCourses({
          category: categoryFromUrl,
          ...(topicFromUrl ? { topic: topicFromUrl } : {}),
          page: 1,
        });
        if (res) setCurrentCourses(res);
      }
    };

    fetchCoursesData();
  }, [location.search, setCredentials, fetchCourses]);

  useEffect(() => {
    if (Object.keys(filters).length === 0) fetchFilter();
  }, []);

  const fetchRecommendedCoursesIfNeeded = useCallback(() => {
    if (access_token && recommendedCourses.results.length === 0) {
      fetchRecommendedCourses(currentTab === "Recommended" ? credentials : {});
    }
  }, [
    access_token,
    recommendedCourses.results.length,
    fetchRecommendedCourses,
  ]);

  const fetchLearningCoursesIfNeeded = useCallback(() => {
    if (access_token && learningCourses.results.length === 0) {
      fetchLearningCourses(currentTab === "My learning" ? credentials : {});
    }
  }, [access_token, learningCourses.results.length]);

  const fetchCoursesIfNeeded = useCallback(() => {
    if (access_token && !isRefreshing && courses.length === 0) {
      const params = new URLSearchParams(location.search);
      const categoryFromUrl = params.get("category");
      if (!categoryFromUrl) {
        fetchCourses({ ...credentials, page: currentPage });
      }
    }
  }, [access_token, isRefreshing, courses.length]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage, currentInfoPage]);

  useEffect(() => {
    fetchRecommendedCoursesIfNeeded();
    fetchLearningCoursesIfNeeded();
    fetchCoursesIfNeeded();
  }, []);

  useEffect(() => {
    if (currentTab === "Recommended") {
      if (recommendedCourses.results.length === 0) {
        setCurrentCourses([]);
      } else {
        setCurrentCourses(recommendedCourses.results);
      }
    }
  }, [recommendedCourses, currentTab, setCurrentCourses]);

  const handleTabChange = async (value: string | null) => {
    if (value === "Recommended") {
      if (currentTab === "My learning") fetchLearningCourses({ page: 1 });
      if (currentTab === "All Courses") fetchCourses({ page: currentPage });
      setCurrentTab("Recommended");
      setCredentials({});
      setCurrentInfoPage(1);
      setCurrentCourses(recommendedCourses.results);
      navigate("/courses");
    } else if (value === "My learning") {
      if (currentTab === "Recommended") fetchRecommendedCourses({ page: 1 });
      if (currentTab === "All Courses") fetchCourses({ page: currentPage });
      setCurrentTab("My learning");
      setCredentials({});
      setCurrentInfoPage(1);
      setCurrentCourses(learningCourses.results.map((item) => item.course));
      navigate("/courses");
    } else if (value === "All Courses") {
      if (currentTab === "My learning") fetchLearningCourses({ page: 1 });
      if (currentTab === "Recommended") fetchRecommendedCourses({ page: 1 });
      setCurrentTab("All Courses");
      setCredentials({});
      setCurrentInfoPage(1);
      setCurrentCourses(courses);
    }
  };

  const handlePageChange = (page: number) => {
    if (currentTab === "All Courses") {
      setCurrentPage(page);
      fetchCoursesData(page);
    } else {
      setCurrentInfoPage(page);
      fetchCoursesData(page);
    }
  };

  const fetchCoursesData = async (page: number) => {
    const newCredentials: GetCoursesParams = { ...credentials, page };
    if (access_token && currentTab === "All Courses") {
      const response = await fetchCourses(newCredentials);
      if (response) setCurrentCourses(response);
    } else if (access_token && currentTab === "My learning") {
      await fetchLearningCourses(newCredentials);
    } else if (access_token && currentTab === "Recommended") {
      const response = await fetchRecommendedCourses(newCredentials);
      if (response) setCurrentCourses(response);
    }
  };

  const handleOnClick = async (id: number) => {
    const course = learningCourses.results.find(
      (learningCourse) => learningCourse.course.id === id
    );
    const exist = learningCourses.results.some(
      (learningCourse) => learningCourse.course.id === id
    );

    if (exist && access_token) {
      await removeLearningCourse(course?.id!, id, credentials);
    } else if (!exist && access_token) {
      await addLearningCourses(id, credentials);
    }
  };

  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    try {
      setCurrentPage(1);
      setCurrentInfoPage(1);
      if (access_token) {
        if (currentTab === "All Courses") {
          const response = await fetchCourses({
            ...credentials,
            page: 1,
          });
          if (response && currentTab === "All Courses")
            setCurrentCourses(response);
        }

        if (currentTab === "Recommended") {
          const response = await fetchRecommendedCourses(credentials);
          if (response) setCurrentCourses(response);
        }

        if (currentTab === "My learning") {
          await fetchLearningCourses(credentials);
        }
      }
    } catch (error) {
      setCredentials({});

      setError(t("filters.error_message")); // Помилка може бути передана через i18n

      console.error("Error during fetch:", error);
    }
  };

  const handleCategoryClick = async (category: string, topic?: string) => {
    setCurrentTab("All Courses");
    setCredentials({ category, topic });
    setCurrentPage(1);
    setCurrentInfoPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });

    const filters: GetCoursesParams = { category };
    if (topic) {
      filters.topic = topic;
    }

    const response = await fetchCourses({ ...filters, page: 1 });
    if (response) setCurrentCourses(response);
  };

  return (
    <MainLayout>
      <ReusableOnboardingTour
        focusRevealProps={focusRevealProps}
        tour={filteredSteps}
        started={started}
        onOnboardingTourEnd={() => {
          tourButtonShow({ hide_tour_tooltip_on_courses: true });
          scrollToTop();
        }}
        maw={mobile ? 310 : 235}
        __size="xs"
      >
        <Box m={mobile ? 16 : 40}>
          <Tabs
            value={currentTab}
            data-onboarding-tour-id="courses-tab"
            onChange={handleTabChange}
          >
            <TabListWithCounts
              currentTab={currentTab}
              recommendedCount={recommendedCourses.count ?? 0}
              learningCount={learningCourses.count ?? 0}
              allCoursesCount={pageInfo.count ?? 0}
            />
          </Tabs>

          <SearchBar
            handleSearch={handleSearch}
            setCredentials={setCredentials}
            credentials={credentials}
          />
          {error && (
            <Notification color="red" onClose={() => setError(null)}>
              {error}
            </Notification>
          )}

          <Grid mt={33} justify={mobile ? "center" : "flex-start"}>
            {currentCourses && currentCourses.length === 0 ? (
              currentTab === "Recommended" ? (
                <Text size="lg" style={{ textAlign: "center" }} color="gray">
                  {t("new_recommended_courses_loading")}
                </Text>
              ) : currentTab === "My learning" ? (
                <Text size="lg" style={{ textAlign: "center" }} color="gray">
                  {t("no_learning_courses")}
                </Text>
              ) : (
                <Text
                  size="lg"
                  ml={20}
                  style={{ textAlign: "center" }}
                  color="gray"
                >
                  {t("no_courses_available")}
                </Text>
              )
            ) : (
              currentCourses.map((course, index) => (
                <Grid.Col
                  span={mobile ? 12 : laptop ? 6 : 4}
                  key={index}
                  style={{
                    minWidth: 320,
                    maxWidth: 370,
                    width: "100%",
                  }}
                  data-onboarding-tour-id={
                    index === 0 ? "courses-card" : undefined
                  }
                >
                  <CourseCard
                    id={course.id}
                    image={
                      course.image
                        ? course.image
                        : images[index % images.length]
                    }
                    platform={course.platform}
                    title={course.title}
                    authors={course.authors}
                    description={course.description}
                    rating={course.rating ?? "Unrated"}
                    handleOnClick={handleOnClick}
                    url={course.url}
                    isLearning={course.is_in_learnings}
                    category={course.category}
                    topics={course.topics}
                    languages={course.languages}
                    organization={course.organization}
                    price={course.price}
                    currency={course.currency}
                    is_free={course.is_free}
                    onCategoryClick={handleCategoryClick}
                  />
                </Grid.Col>
              ))
            )}
          </Grid>

          <Box mt={30} style={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              value={
                currentTab === "All Courses" ? currentPage : currentInfoPage
              }
              onChange={handlePageChange}
              total={Math.ceil(
                currentTab === "All Courses"
                  ? (pageInfo?.count || 0) / 21
                  : currentTab === "Recommended"
                  ? (recommendedCourses.count || 0) / 21
                  : (learningCourses.count || 0) / 21
              )}
            />
          </Box>
        </Box>
      </ReusableOnboardingTour>
      <TourButton
        onClick={() => {
          tourButtonShow({ hide_tour_tooltip_on_courses: false });
        }}
      />
    </MainLayout>
  );
};
